const industries = [
  {
    icon: 'ri-home-gear-line',
    name: 'Home Services',
    headline: 'Capture every call. Book every job.',
    desc: 'Maximize technician utilization with AI-powered call handling and automated scheduling that fills your calendar without lifting a finger.',
    img: 'https://public.readdy.ai/ai/img_res/1514110a8bbeef4f77bf41b51ba2b906.jpg',
    color: 'from-orange-500/20 to-amber-500/10',
    accent: 'text-orange-400',
    border: 'border-orange-500/20',
  },
  {
    icon: 'ri-scales-line',
    name: 'Legal',
    headline: 'Instant lead response and qualification.',
    desc: 'Never miss a case again. Automated intake, instant response, and intelligent qualification ensure every inquiry gets the attention it deserves.',
    img: 'https://readdy.ai/api/search-image?query=modern%20law%20office%20interior%20with%20professional%20attorney%20at%20desk%20reviewing%20documents%2C%20clean%20minimal%20design%2C%20dark%20wood%20and%20white%20tones%2C%20professional%20photography&width=600&height=400&seq=ind-legal&orientation=landscape',
    color: 'from-slate-500/20 to-gray-500/10',
    accent: 'text-slate-300',
    border: 'border-slate-500/20',
  },
  {
    icon: 'ri-heart-pulse-line',
    name: 'Healthcare & Medical',
    headline: 'Automate patient intake and scheduling.',
    desc: 'Streamline workflows from first contact to appointment confirmation. Reduce admin workload and improve patient experience simultaneously.',
    img: 'https://readdy.ai/api/search-image?query=modern%20medical%20clinic%20reception%20area%20with%20clean%20white%20interior%2C%20professional%20healthcare%20staff%20at%20desk%2C%20bright%20welcoming%20environment%2C%20contemporary%20design&width=600&height=400&seq=ind-health&orientation=landscape',
    color: 'from-violet-500/20 to-indigo-500/10',
    accent: 'text-violet-400',
    border: 'border-violet-500/20',
  },
  {
    icon: 'ri-building-line',
    name: 'Real Estate',
    headline: 'Respond instantly. Convert more opportunities.',
    desc: 'Speed-to-lead is everything in real estate. Our AI responds to every inquiry within seconds, qualifies buyers and sellers, and books showings automatically.',
    img: 'https://readdy.ai/api/search-image?query=luxury%20modern%20real%20estate%20property%20exterior%20with%20clean%20architecture%2C%20professional%20real%20estate%20photography%2C%20bright%20daylight%2C%20upscale%20residential%20building&width=600&height=400&seq=ind-realestate&orientation=landscape',
    color: 'from-emerald-500/20 to-green-500/10',
    accent: 'text-emerald-400',
    border: 'border-emerald-500/20',
  },
  {
    icon: 'ri-line-chart-line',
    name: 'Financial Services',
    headline: 'Structure lead flow and maintain compliance.',
    desc: 'Automate outreach, structure your pipeline, and maintain compliance — all within a unified system designed for regulated industries.',
    img: 'https://readdy.ai/api/search-image?query=professional%20financial%20advisor%20office%20with%20modern%20interior%2C%20charts%20and%20data%20on%20screens%2C%20clean%20corporate%20environment%2C%20professional%20business%20photography&width=600&height=400&seq=ind-finance&orientation=landscape',
    color: 'from-indigo-500/20 to-violet-500/10',
    accent: 'text-indigo-400',
    border: 'border-indigo-500/20',
  },
];

export default function Industries() {
  return (
    <section id="industries" className="bg-[#0c0e24] py-24 md:py-32 relative overflow-hidden">
      {/* Subtle glow */}
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full bg-violet-600/5 blur-3xl pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">
        {/* Header */}
        <div className="flex flex-col lg:flex-row items-start gap-8 mb-16 md:mb-20">
          <div className="flex-1">
            <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-1.5 mb-6">
              <span className="w-2 h-2 rounded-full bg-white/40 flex-shrink-0"></span>
              <span className="text-white/50 text-xs font-medium tracking-widest uppercase whitespace-nowrap">Industries</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
              Built for High-Value<br />
              <span className="italic font-light text-white/30">Service Businesses</span>
            </h2>
          </div>
          <div className="flex-1 lg:pt-12">
            <p className="text-white/45 text-base leading-relaxed">
              We specialize in industries where speed, consistency, and follow-through directly translate to revenue. If your business runs on leads and appointments, we built this for you.
            </p>
          </div>
        </div>

        {/* Industry cards: 3 top + 2 bottom */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-5">
          {industries.slice(0, 3).map((ind, i) => (
            <IndustryCard key={i} industry={ind} />
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {industries.slice(3).map((ind, i) => (
            <IndustryCard key={i} industry={ind} wide />
          ))}
        </div>
      </div>
    </section>
  );
}

function IndustryCard({ industry, wide }: { industry: typeof industries[0]; wide?: boolean }) {
  return (
    <div className={`rounded-2xl overflow-hidden border ${industry.border} bg-white/3 hover:bg-white/5 transition-all group`}>
      {/* Image */}
      <div className={`w-full overflow-hidden ${wide ? 'h-44' : 'h-48'}`}>
        <img
          src={industry.img}
          alt={industry.name}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      {/* Content */}
      <div className="p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-br ${industry.color} flex-shrink-0`}>
            <i className={`${industry.icon} ${industry.accent} text-base`}></i>
          </div>
          <span className={`text-xs font-bold ${industry.accent} uppercase tracking-wider whitespace-nowrap`}>{industry.name}</span>
        </div>
        <h3 className="text-white font-bold text-sm mb-2">{industry.headline}</h3>
        <p className="text-white/45 text-xs leading-relaxed">{industry.desc}</p>
      </div>
    </div>
  );
}
